import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, FlatList, Modal, Platform, Pressable, View } from 'react-native';
import { countries } from './Resources';
import { CoreConfig } from '../../../CoreConfig';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { component } from '../../Decorators/Component';
import { CardView } from '../Cards/CardView';
import { InputView } from './InputView';
import { ParagraphView } from '../Typography/Paragraph';
import { H6View } from '../Typography/H6View';
import { TertiaryButtonView } from '../Buttons/TertiaryButtonView';
import { CountryView } from './CountryView';
import { ThemeContext } from '../../Contexts/ThemeContext';
import { getColorScheme } from '../../Hooks/UseColorScheme';
export const CountriesListView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.countryList,
    style(theme, mediaQuery, window, props) {
        return {
            container: {
                zIndex: 1,
                paddingTop: 2,
                maxHeight: 200,
                position: 'absolute',
                bottom: props.bottomOffset,
                left: props.leftOffset || 0,
                right: props.rightOffset || 0,
                top: props.topOffset || 0,
                backgroundColor: '#fff',
                borderStyle: 'solid',
                borderColor: 'rgb(191, 204, 218)',
                borderWidth: 1,
                borderRadius: 8
            },
            modal: {
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center'
            },
            card: {
                width: '90%',
                maxHeight: '100%'
            },
            list: {
                marginTop: 10,
                marginBottom: 20,
                height: window.height * 0.5,
            },
            image: {
                marginEnd: 10
            },
            listItem: {
                paddingVertical: 5,
                paddingHorizontal: 10,
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center'
            },
            listItemText: {
                flex: 1,
                marginBottom: 0
            },
            dialCode: {
                marginBottom: 0
            },
            input: {
                paddingHorizontal: 5
            }
        };
    }
})(({ style, onPress, onClose, visible, localization }) => {
    const listAnimation = useRef(new Animated.Value(0)).current;
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState('');
    useEffect(() => {
        if (!visible) {
            return Animated.timing(listAnimation, {
                useNativeDriver: false,
                toValue: 0,
                duration: 200
            }).start(() => {
                setIsOpen(false);
            });
        }
        setIsOpen(true);
        Animated.timing(listAnimation, {
            useNativeDriver: false,
            toValue: 1,
            duration: 200
        }).start();
    }, [visible]);
    const values = useMemo(() => {
        const search = text.toLowerCase();
        return countries.filter(item => {
            return item.name.toLowerCase().includes(search) || item.dialCode.toLowerCase().includes(search);
        });
    }, [text]);
    const handlePress = useCallback((value) => onPress === null || onPress === void 0 ? void 0 : onPress(value), [onPress]);
    const handleInput = useCallback((text) => setText(text), []);
    const colorScheme = useMemo(() => {
        if (Platform.OS !== 'web') {
            return getColorScheme().value;
        }
        return Object.assign(Object.assign({}, getColorScheme().value), { scheme: 'default', colors: Object.assign(Object.assign({}, getColorScheme().value.colors), { background: '#fff', default_text: '#394e66', text: '#394e66' }) });
    }, [getColorScheme().value]);
    if (Platform.OS !== 'web') {
        return (_jsx(ThemeContext.Provider, { value: colorScheme, children: _jsx(Modal, { visible: visible, transparent: true, animationType: "slide", children: _jsx(View, { style: style === null || style === void 0 ? void 0 : style.modal, children: _jsxs(CardView, { containerStyles: style === null || style === void 0 ? void 0 : style.card, children: [_jsx(InputView, { value: text, onChangeText: handleInput, placeholder: (localization === null || localization === void 0 ? void 0 : localization.input_label) || (localization === null || localization === void 0 ? void 0 : localization.select_country) || 'Select country' }), _jsx(FlatList, { style: style === null || style === void 0 ? void 0 : style.list, data: values, keyExtractor: (item) => item.iso2, keyboardShouldPersistTaps: "handled", renderItem: ({ item }) => (_jsxs(Pressable, { style: style === null || style === void 0 ? void 0 : style.listItem, onPress: () => handlePress(item), children: [_jsx(CountryView, { code: item.iso2, containerStyle: style === null || style === void 0 ? void 0 : style.image }), _jsx(ParagraphView, { textStyle: style === null || style === void 0 ? void 0 : style.listItemText, numberOfLines: 1, children: item.name }), _jsx(H6View, { textStyle: style === null || style === void 0 ? void 0 : style.dialCode, children: item.dialCode })] })) }), _jsx(View, { children: _jsx(TertiaryButtonView, { text: (localization === null || localization === void 0 ? void 0 : localization.close) || 'Close', onPress: onClose }) })] }) }) }) }));
    }
    return isOpen
        ? (_jsx(ThemeContext.Provider, { value: colorScheme, children: _jsxs(Animated.View, { style: [style === null || style === void 0 ? void 0 : style.container, { opacity: listAnimation }], children: [_jsx(InputView, { containerStyle: style === null || style === void 0 ? void 0 : style.input, value: text, onChangeText: handleInput, placeholder: (localization === null || localization === void 0 ? void 0 : localization.input_label) || (localization === null || localization === void 0 ? void 0 : localization.select_country) || 'Select country' }), _jsx(Animated.FlatList, { style: { flex: 1 }, data: values, keyExtractor: (item) => item.iso2, renderItem: ({ item }) => (_jsxs(Pressable, { style: style === null || style === void 0 ? void 0 : style.listItem, onPress: () => handlePress(item), children: [_jsx(CountryView, { code: item.iso2, containerStyle: style === null || style === void 0 ? void 0 : style.image }), _jsx(ParagraphView, { textStyle: style === null || style === void 0 ? void 0 : style.listItemText, light: false, children: item.name }), _jsx(H6View, { textStyle: style === null || style === void 0 ? void 0 : style.dialCode, light: false, children: item.dialCode })] })) })] }) }))
        : null;
});
